import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Trash2Icon } from 'lucide-react';
import { expiryAlertWindow, isExpired } from './pharmacyUtils';
import { DisposeStockModal } from './DisposeStockModal';
import { DisposalHistory } from './DisposalHistory';
export function ExpiredMedicines() {
  const {
    currentUser,
    medicineBatches,
    medicines
  } = useData();
  const [message, setMessage] = useState('');
  const manager = currentUser?.role === 'Pharmacy Manager';
  const groups = useMemo(() => ({
    // Fully disposed batches (0 left) drop off the list automatically.
    expired: medicineBatches.filter(batch => batch.quantityRemaining > 0 && expiryAlertWindow(batch) === 'Expired'),
    days30: medicineBatches.filter(batch => expiryAlertWindow(batch) === '30 Days'),
    days60: medicineBatches.filter(batch => expiryAlertWindow(batch) === '60 Days'),
    days90: medicineBatches.filter(batch => expiryAlertWindow(batch) === '90 Days')
  }), [medicineBatches]);
  const [disposingBatchId, setDisposingBatchId] = useState<string | null>(null);
  const disposingBatch = medicineBatches.find(item => item.id === disposingBatchId);
  const dispose = (batchId: string) => {
    setMessage('');
    setDisposingBatchId(batchId);
  };
  return <div className="space-y-6" data-id="element-8775" data-mp="6mpyasq8x9tk">
      <div data-id="element-8776" data-mp="bjgbn8ao1rba">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-8777" data-mp="bj6ortkx409t">Expiry Management</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-8778" data-mp="git2030qxgim">
          Expiry alerts update live at 90, 60, and 30 days. Expired batches are
          blocked from dispensing until disposal.
        </p>
      </div>
      {message && <p role="status" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700" data-id="element-8779" data-mp="1ryrd0cfhicmh">
          {message}
        </p>}
      <BatchTable title="Expired — dispensing blocked" tone="red" batches={groups.expired} medicines={medicines} action={manager ? dispose : undefined} data-id="element-8780" data-mp="3t07dw1oz91e7" />
      {!manager && groups.expired.length > 0 && <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-id="element-8781" data-mp="5u6pg129lpap">
          Expired inventory remains blocked. A Pharmacy Manager must authorise
          disposal.
        </p>}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3" data-id="element-8782" data-mp="3a9y0rwrnjp7q">
        <BatchTable title="Within 30 days" tone="orange" batches={groups.days30} medicines={medicines} data-id="element-8783" data-mp="35bo5lm5xgom4" />
        <BatchTable title="Within 60 days" tone="amber" batches={groups.days60} medicines={medicines} data-id="element-8784" data-mp="3j3unbcoqb3no" />
        <BatchTable title="Within 90 days" tone="amber" batches={groups.days90} medicines={medicines} data-id="element-8785" data-mp="2qkno6rn7dkdu" />
      </div>
      {manager && <DisposalHistory data-id="element-8786" data-mp="2cfnccy0vz5ph" />}
      {disposingBatch && <DisposeStockModal batch={disposingBatch} medicineName={medicines.find(item => item.id === disposingBatch.medicineId)?.name || 'Unknown medicine'} onClose={() => setDisposingBatchId(null)} data-id="element-8787" data-mp="2cevne61so686" />}
    </div>;
}
function BatchTable({
  title,
  tone,
  batches,
  medicines,
  action
}: {
  title: string;
  tone: 'red' | 'orange' | 'amber';
  batches: ReturnType<typeof useData>['medicineBatches'];
  medicines: ReturnType<typeof useData>['medicines'];
  action?: (batchId: string) => void;
}) {
  const toneClass = tone === 'red' ? 'border-red-200 bg-red-50 text-red-800' : tone === 'orange' ? 'border-orange-200 bg-orange-50 text-orange-800' : 'border-amber-200 bg-amber-50 text-amber-800';
  return <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-8788" data-mp="1esevjgbtyktt">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-8789" data-mp="1eq26ykbkhc7s">
        <div data-id="element-8790" data-mp="1v1e1s7la47dg">
          <h3 className="font-bold text-slate-900" data-id="element-8791" data-mp="1q94ekzbjtlev">{title}</h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-8792" data-mp="1q7jl7d0z08il">
            {batches.length} active batch{batches.length === 1 ? '' : 'es'}{' '}
            require attention
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${toneClass}`} data-id="element-8793" data-mp="1gc80me2vs72x">
          {batches.reduce((sum, batch) => sum + batch.quantityRemaining, 0)}{' '}
          units
        </span>
      </div>
      <div className="overflow-x-auto" data-id="element-8794" data-mp="24094h9p46zc0">
        <table className="min-w-full divide-y divide-slate-100" data-id="element-8795" data-mp="23z7wje3easyi">
          <thead className="bg-slate-50" data-id="element-8796" data-mp="1z1eoefij7ovl">
            <tr data-id="element-8797" data-mp="1z0d4rmng9u29">
              {['Medicine', 'Batch', 'Expiry date', 'Blocked / available units', ...(action ? ['Action'] : [])].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-8798" data-mp="2z9culvl296ql">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-8799" data-mp="395ftmskm8p9b">
            {batches.length ? batches.map(batch => {
            const medicine = medicines.find(item => item.id === batch.medicineId);
            const blocked = isExpired(batch);
            return <tr key={batch.id} data-id="element-8800" data-mp="3rxidsdwgunob">
                    <td className="px-5 py-3.5 font-semibold text-slate-900" data-id="element-8801" data-mp="3ryjpmklb3e73">
                      {medicine?.name || 'Unknown medicine'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-8802" data-mp="1xkip7va3yj4w">
                      {batch.batchNumber}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-8803" data-mp="22jlvtk5qkxez">
                      {new Date(`${batch.expiryDate}T00:00:00`).toLocaleDateString()}
                    </td>
                    <td className={`px-5 py-3.5 text-sm font-semibold ${blocked ? 'text-red-700' : 'text-slate-900'}`} data-id="element-8804" data-mp="1qj5dumxh4p52">
                      {batch.quantityRemaining}{' '}
                      {blocked ? 'blocked' : 'available'}
                    </td>
                    {action && <td className="px-5 py-3.5 text-right" data-id="element-8805" data-mp="29ggeaauudom3">
                        <Button size="sm" variant="danger" onClick={() => action(batch.id)} data-id="element-8806" data-mp="29du5y904n52t">
                          <Trash2Icon className="mr-1.5 h-4 w-4" data-id="element-8807" data-mp="2iabc0xtmwcrp" /> Dispose
                        </Button>
                      </td>}
                  </tr>;
          }) : <tr data-id="element-8808" data-mp="3breux1bd41da">
                <td colSpan={action ? 5 : 4} className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-8809" data-mp="3bszooopurga3">
                  No batches in this alert window.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}